import { Button } from "@bcc/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { FileText, LucideIcon, Package, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setLabelTemplatesTestId, setLabelTemplatesTitleTestId } from "./label-templates.utils";

interface TemplateData {
  id: number;
  name: string;
  description: string;
  icon: string;
  uses: number;
}

interface LabelTemplatesProps {
  data: TemplateData[];
  testId?: string;
}

const iconMap: Record<string, LucideIcon> = {
  Tag,
  Package,
  FileText,
};

interface TemplateCardProps {
  template: TemplateData;
  onUseTemplate: () => void;
  index: number;
}

function TemplateCard({ template, onUseTemplate, index }: Readonly<TemplateCardProps>) {
  const IconComponent = iconMap[template.icon] || Tag;
  const { t } = useTranslation();

  return (
    <Card className="hover:shadow-md transition-shadow" testId={setLabelTemplatesTestId(`card-${index}`)}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <IconComponent className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base" testId={setLabelTemplatesTestId(`name-${index}`)}>
              {template.name}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1" data-testid={setLabelTemplatesTestId(`uses-${index}`)}>
              {t("views.labelTemplates.uses", { count: template.uses })}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4" data-testid={setLabelTemplatesTestId(`description-${index}`)}>
          {template.description}
        </p>
        <Button
          className="w-full bg-transparent"
          variant="outline"
          onClick={onUseTemplate}
          data-testid={setLabelTemplatesTestId(`use-button-${index}`)}
        >
          {t("views.labelTemplates.useTemplate")}
        </Button>
      </CardContent>
    </Card>
  );
}

export function LabelTemplates({ data, testId }: Readonly<LabelTemplatesProps>) {
  const handleUseTemplate = (templateId: number) => {
    console.log("Use template:", templateId);
  };
  const { t } = useTranslation();

  return (
    <div data-testid={testId ?? setLabelTemplatesTestId("container")}>
      <h2 className="text-xl font-semibold mb-4" data-testid={setLabelTemplatesTitleTestId()}>
        {t("views.labelTemplates.title")}
      </h2>
      <div className="grid gap-4 md:grid-cols-3">
        {data.map((template, index) => (
          <TemplateCard key={template.id} template={template} onUseTemplate={() => handleUseTemplate(template.id)} index={index} />
        ))}
      </div>
    </div>
  );
}
